
import React, { useEffect } from 'react';
import { ListChecks, Copy, Share2, ThumbsUp, ThumbsDown } from 'lucide-react';
import { useArticleAI } from '@/contexts/article-ai/ArticleAIContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { applyMarkdownFormatting } from '@/utils/textFormatting';

interface ArticleKeyTakeawaysProps {
  article: any;
}

const ArticleKeyTakeaways: React.FC<ArticleKeyTakeawaysProps> = ({ article }) => {
  const { generateKeyTakeaways, takeaways, isTakeawaysLoading, provideFeedback } = useArticleAI();
  const { toast } = useToast();

  useEffect(() => {
    if (!takeaways && !isTakeawaysLoading) {
      const articleContent = typeof article.content === 'string' 
        ? article.content 
        : article.editor_data?.blocks.map((block: any) => {
            if (block.type === 'paragraph') return block.data?.text || '';
            if (block.type === 'header') return block.data?.text || '';
            return '';
          }).join('\n\n') || '';
          
      generateKeyTakeaways(articleContent, article.title);
    }
  }, [article, generateKeyTakeaways, takeaways, isTakeawaysLoading]);

  const handleCopy = () => {
    if (!takeaways) return;
    
    const takeawaysText = takeaways.map(point => `• ${point}`).join('\n');
    navigator.clipboard.writeText(`Key Takeaways from "${article.title}":\n\n${takeawaysText}`);
    
    toast({
      title: "Copied to clipboard",
      description: "Key takeaways have been copied to your clipboard",
    });
  };

  const handleShare = () => {
    if (!takeaways) return;
    
    // In a real implementation, this would generate an image or prepare for social sharing
    toast({
      title: "Share feature",
      description: "This would share the takeaways as an image (feature in development)",
    });
  };

  return (
    <Card className="w-full border-primary/20">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <ListChecks className="h-5 w-5 mr-2 text-primary" />
            <CardTitle className="text-lg">Key Takeaways</CardTitle>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" onClick={handleCopy} title="Copy takeaways">
              <Copy className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={handleShare} title="Share takeaways">
              <Share2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <CardDescription>
          AI-generated summary of the main points
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isTakeawaysLoading ? (
          <div className="py-6 flex justify-center items-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : takeaways && takeaways.length > 0 ? (
          <ul className="space-y-2 py-1">
            {takeaways.map((point, index) => (
              <li key={index} className="flex gap-2">
                <span className="text-primary font-bold">•</span>
                <span 
                  dangerouslySetInnerHTML={{ 
                    __html: applyMarkdownFormatting(point)
                  }}
                ></span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground text-center py-4">Unable to generate takeaways</p>
        )}
      </CardContent>
      <Separator />
      <CardFooter className="pt-2 flex justify-between">
        <div className="text-xs text-muted-foreground">
          Generated by AI based on article content
        </div>
        <div className="flex gap-1">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-7 w-7" 
            title="This was helpful"
            onClick={() => provideFeedback('takeaways', 'takeaways', 'helpful')}
          >
            <ThumbsUp className="h-3 w-3" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-7 w-7" 
            title="This wasn't helpful"
            onClick={() => provideFeedback('takeaways', 'takeaways', 'not-helpful')}
          >
            <ThumbsDown className="h-3 w-3" />
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default ArticleKeyTakeaways;
